<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'NumberingSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    objectType: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: false,
    },
    modelValue: {
      type: null,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    bindObject: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const internalValue = ref(null);
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_filterable = computed(() => false);
  const b_7315092382398562_searchable = computed(() => false);

  const $data = computed(() => ({
    internalValue: internalValue.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_filterable: b_7315092382398562_filterable.value,
    b_7315092382398562_searchable: b_7315092382398562_searchable.value,
  }));

  const $$load_internalValue = () => {
    return props.bindObject
      ? props.modelValue
        ? props.modelValue.id
        : null
      : props.modelValue;
  };
  const $$load_items = () => {
    return $fn.value
      .fetch('list_numbering', {
        parameters: { resource_type: props.objectType },
      })
      .then((d) =>
        [{ id: null, description: $fn.value.gettext('Manual') }].concat(d)
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'internalValue' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_filterable', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_searchable', nodeId: '7315092382398562' },
    ],
    loaders: { internalValue: $$load_internalValue, items: $$load_items },
    ux: {},
    dataRefs: {
      internalValue,
      items,
      ux,
      b_7315092382398562_filterable,
      b_7315092382398562_searchable,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.objectType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('objectType');
    }
  );
  watch(
    () => props.name,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('name');
    }
  );
  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.bindObject,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('bindObject');
    }
  );

  const act_7315092382398562_emit_kng3gbwkd_getActionArgs = async ($event) => {
    return {
      event: 'update:modelValue',
      value: props.bindObject
        ? { id: $event.originalEvent }
        : $event.originalEvent,
    };
  };
  const act_3343864469950044_register_7lrx0we4p_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultID',
      value:
        $event.data.modalResult &&
        $event.data.modalResult.ok &&
        $event.data.modalResult.value.id,
    };
  };
  const inst_evh_3343864469950044_close_99zja89of_getWhen = async ($event) => {
    return $event.data.resultID;
  };
  const act_3343864469950044_setData_v7pa2xkbq_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'internalValue',
      value: resultID,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_3343864469950044_close_c0xbv2wjq_getWhen = async ($event) => {
    return $event.data.resultID;
  };
  const act_3343864469950044_showModal_4ckgmu6s8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Numbering',
      props: {
        objectType: props.objectType,
      },
    };
  };

  const act_7315092382398562_emit_kng3gbwkd = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_kng3gbwkd_getActionArgs,
  });
  const inst_evh_7315092382398562_input_enieeyyq2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_kng3gbwkd,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_7315092382398562_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_input_enieeyyq2],
    event: 'input',
  });
  const act_3343864469950044_register_7lrx0we4p =
    new actionTypes.RegisterAction({
      actionArgs: act_3343864469950044_register_7lrx0we4p_getActionArgs,
    });
  const inst_evh_3343864469950044_close_3pbzypusu =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_register_7lrx0we4p,
      event: 'close',
      displayName: 'register',
    });
  const act_3343864469950044_reloadComponentData_520hpxgav =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_3343864469950044_close_99zja89of =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_reloadComponentData_520hpxgav,
      event: 'close',
      when: inst_evh_3343864469950044_close_99zja89of_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_3343864469950044_setData_v7pa2xkbq = new actionTypes.SetDataAction({
    actionArgs: act_3343864469950044_setData_v7pa2xkbq_getActionArgs,
  });
  const inst_evh_3343864469950044_close_c0xbv2wjq =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_setData_v7pa2xkbq,
      event: 'close',
      when: inst_evh_3343864469950044_close_c0xbv2wjq_getWhen,
      displayName: 'setData',
    });
  const act_3343864469950044_showModal_4ckgmu6s8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3343864469950044_showModal_4ckgmu6s8_getActionArgs,
      events: [
        inst_evh_3343864469950044_close_3pbzypusu,
        inst_evh_3343864469950044_close_99zja89of,
        inst_evh_3343864469950044_close_c0xbv2wjq,
      ],
    });
  const inst_evh_3343864469950044_click_bm12z7k95 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_showModal_4ckgmu6s8,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_3343864469950044_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_click_bm12z7k95],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_input_enieeyyq2,
    inst_evh_7315092382398562_input,
    inst_evh_3343864469950044_close_3pbzypusu,
    inst_evh_3343864469950044_close_99zja89of,
    inst_evh_3343864469950044_close_c0xbv2wjq,
    inst_evh_3343864469950044_click_bm12z7k95,
    inst_evh_3343864469950044_click,
  };

  const evh_7315092382398562_input_enieeyyq2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input_enieeyyq2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_input = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_3pbzypusu = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_3pbzypusu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_99zja89of = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_99zja89of.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_c0xbv2wjq = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_c0xbv2wjq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click_bm12z7k95 = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click_bm12z7k95.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormSearchableSelect
    v-model="internalValue"
    :name="name"
    valueField="id"
    textField="description"
    :filterable="b_7315092382398562_filterable"
    :searchable="b_7315092382398562_searchable"
    :options="items"
    :readonly="readonly"
    @input="evh_7315092382398562_input($event, {})"
  >
    <template #list-footer>
      <hr />
      <ItpLink
        :text="gettext('Add new...')"
        class="dropdown-item"
        @click="evh_3343864469950044_click($event, {})"
      >
      </ItpLink>
    </template>
  </ItpFormSearchableSelect>
</template>
